import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FaHeart, FaLanguage, FaBook, FaPlay, FaLock, FaUnlock, FaChevronDown } from 'react-icons/fa';
import ReportButton from '../components/reports/ReportButton';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: 'english' | 'spanish' | 'french';
  song_lyrics: string;
  translation: string | null;
  song_vocabulary: any;
  song_likes: number;
  is_promoted: boolean;
  is_featured: boolean;
  is_free: boolean;
  user_has_liked?: boolean;
  category?: string | null;
}

export default function Songs() {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [filteredSongs, setFilteredSongs] = useState<Song[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, maxHeight: 300 });
  const navigate = useNavigate();
  const user = getCurrentUser();

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'drama', label: 'Drama' },
    { value: 'sadness', label: 'Sadness' },
    { value: 'presentation', label: 'Presentation' },
    { value: 'family', label: 'Family' },
    { value: 'nature', label: 'Nature' },
    { value: 'business', label: 'Business' },
    { value: 'fun', label: 'Fun' },
    { value: 'other', label: 'Other' }
  ];

  // Refs for dropdown positioning
  const dropdownButtonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position dropdown within viewport
  const calculateDropdownPosition = () => {
    if (!dropdownButtonRef.current) return;
    const buttonRect = dropdownButtonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const dropdownWidth = 200;
    const dropdownMaxHeight = 300;

    let top = buttonRect.bottom + 8;
    let left = buttonRect.left;
    let maxHeight = dropdownMaxHeight;

    if (top + dropdownMaxHeight > viewportHeight) {
      top = buttonRect.top - dropdownMaxHeight - 8;
      if (top < 0) {
        top = 8;
        maxHeight = viewportHeight - 16;
      }
    }

    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 16;
    }
    if (left < 16) left = 16;

    setDropdownPosition({ top, left, maxHeight });
  };

  const handleCategoryDropdownToggle = () => {
    if (!showCategoryDropdown) calculateDropdownPosition();
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (categoryValue: string) => {
    setSelectedCategory(categoryValue);
    setShowCategoryDropdown(false);
  };

  useEffect(() => {
    loadSongs();
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterSongs();
  }, [songs, selectedCategory]);

  const checkAccess = async () => {
    if (!user) return;
    try {
      if ((user as any).is_admin) {
        setHasAccess(true);
        return;
      }
      const { data: couponUsage, error: couponError } = await supabase
        .from('stripeencouponusage')
        .select('*')
        .eq('user_id', (user as any).id)
        .gte('valid_until', new Date().toISOString());

      if (couponError) throw couponError;
      setHasAccess(couponUsage && couponUsage.length > 0);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const filterSongs = () => {
    if (selectedCategory === 'all') {
      setFilteredSongs(songs);
    } else {
      setFilteredSongs(songs.filter((song) => song.category === selectedCategory));
    }
  };

  const loadSongs = async () => {
    try {
      const { data, error } = await supabase
        .from('stripeensongs')
        .select('*')
        .eq('is_visible', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const songsData = (data as Song[]) || [];

      if (user) {
        const songIds = songsData.map((s) => s.id);
        if (songIds.length > 0) {
          const { data: likedSongs } = await supabase
            .from('stripeen_like_songs')
            .select('song_id')
            .eq('user_id', (user as any).id)
            .in('song_id', songIds);

          const likedSongIds = new Set((likedSongs || []).map((l: any) => l.song_id));
          setSongs(
            songsData.map((s) => ({
              ...s,
              user_has_liked: likedSongIds.has(s.id),
            }))
          );
        } else {
          setSongs(songsData);
        }
      } else {
        setSongs(songsData);
      }
    } catch (error: any) {
      console.error(error);
      toast.error('Error loading songs');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (song: Song) => {
    if (!user) {
      toast.error('Please log in to access songs');
      navigate('/login');
      return;
    }
    if (!hasAccess && !song.is_free) {
      setShowAccessModal(true);
      return;
    }
    navigate(`/songs/${song.id}`);
  };

  const handleBuyCoupon = () => {
    navigate('/coupons');
  };

  const handleLike = async (songId: string) => {
    if (!user) {
      toast.error('Please log in to like songs');
      return;
    }
    try {
      const { data: existingLike, error: checkError } = await supabase
        .from('stripeen_like_songs')
        .select('id')
        .eq('song_id', songId)
        .eq('user_id', (user as any).id)
        .maybeSingle();

      if (checkError && (checkError as any).code !== 'PGRST116') throw checkError;

      if (existingLike) {
        const { error: deleteError } = await supabase
          .from('stripeen_like_songs')
          .delete()
          .eq('song_id', songId)
          .eq('user_id', (user as any).id);
        if (deleteError) throw deleteError;

        const { error: updateError } = await supabase.rpc('decrement_ensong_likes', { song_id: songId });
        if (updateError) throw updateError;

        setSongs((prev) =>
          prev.map((s) => (s.id === songId ? { ...s, song_likes: s.song_likes - 1, user_has_liked: false } : s))
        );
        toast.success('Like removed!');
      } else {
        const { error: insertError } = await supabase.from('stripeen_like_songs').insert({
          song_id: songId,
          user_id: (user as any).id,
        });
        if (insertError) throw insertError;

        const { error: updateError } = await supabase.rpc('increment_ensong_likes', { song_id: songId });
        if (updateError) throw updateError;

        setSongs((prev) =>
          prev.map((s) => (s.id === songId ? { ...s, song_likes: s.song_likes + 1, user_has_liked: true } : s))
        );
        toast.success('Song liked!');
      }
    } catch (error: any) {
      console.error('Error handling like:', error);
      toast.error('Failed to update like');
    }
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-100">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Language Learning Songs</h1>
          <p className="mt-2 text-gray-600">Learn languages through music</p>
        </div>
      </div>

      {/* Access Status */}
      {!user?.is_admin && (
        <div className="mb-8 p-4 rounded-lg bg-black">
          <div className="flex items-center">
            <div className={`rounded-full p-2 mr-3 ${hasAccess ? 'bg-yellow-500' : 'bg-yellow-500'}`}>
              {hasAccess ? <FaPlay className="w-5 h-5 text-black" /> : <FaLock className="w-5 h-5 text-black" />}
            </div>
            <div>
              <h3 className={`font-semibold ${hasAccess ? 'text-yellow-400' : 'text-yellow-400'}`}>
                {hasAccess ? 'You have access to all songs' : 'Limited Access'}
              </h3>
              <p className={`text-sm ${hasAccess ? 'text-white' : 'text-white'}`}>
                {hasAccess
                  ? 'Enjoy learning languages through music!'
                  : 'Purchase a coupon to access all premium songs. Free songs are still available!'}
              </p>
            </div>
            {!hasAccess && (
              <button
                onClick={handleBuyCoupon}
                className="ml-auto bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-semibold"
              >
                Buy Access
              </button>
            )}
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filter by Category</h2>

        {/* Mobile Custom Dropdown */}
        <div className="md:hidden relative">
          <button
            ref={dropdownButtonRef}
            onClick={handleCategoryDropdownToggle}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 flex items-center justify-between"
          >
            <span>{categories.find((c) => c.value === selectedCategory)?.label || 'All Categories'}</span>
            <FaChevronDown className={`w-4 h-4 transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''}`} />
          </button>

          {/* Custom Dropdown Menu */}
          {showCategoryDropdown && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-40" onClick={() => setShowCategoryDropdown(false)} />

              {/* Dropdown Content */}
              <div
                ref={dropdownRef}
                className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: '200px',
                  maxHeight: `${dropdownPosition.maxHeight}px`,
                }}
              >
                <div className="overflow-y-auto" style={{ maxHeight: `${dropdownPosition.maxHeight - 16}px` }}>
                  {categories.map((category) => (
                    <button
                      key={category.value}
                      onClick={() => handleCategorySelect(category.value)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0 ${
                        selectedCategory === category.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-900'
                      }`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Desktop Buttons */}
        <div className="hidden md:flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => handleCategorySelect(category.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                selectedCategory === category.value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Songs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {loading ? (
          Array(6)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <Skeleton height={200} />
                <div className="p-4">
                  <Skeleton count={3} />
                </div>
              </div>
            ))
        ) : (
          filteredSongs.map((song) => (
            <div key={song.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="relative">
                <img src={song.song_cover} alt={song.creator} className="w-full h-48 object-cover" />
                <button
                  onClick={() => handlePlay(song)}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-200"
                >
                  {hasAccess || song.is_free ? (
                    <FaPlay className="w-12 h-12 text-white" />
                  ) : (
                    <div className="flex flex-col items-center">
                      <FaLock className="w-12 h-12 text-white mb-2" />
                      <span className="text-white text-sm font-medium">Buy Access</span>
                    </div>
                  )}
                </button>

                {/* Category badge (only this is inside the conditional) */}
                {song.category && (
                  <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-1.5 py-0.5 rounded-full text-xs font-medium">
                    {categories.find((c) => c.value === song.category)?.label || song.category}
                  </div>
                )}

                {song.is_free && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    <FaUnlock className="w-4 h-4 mr-1" />
                    Free
                  </div>
                )}
              </div>

              <div className="p-4">
                {song.category && (
                  <div className="mb-2">
                    <span className="inline-block bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">
                      {categories.find((c) => c.value === song.category)?.label || song.category}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{song.creator}</h3>
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">{song.language}</span>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => handleLike(song.id)}
                    className={`flex items-center transition-colors duration-200 group ${
                      song.user_has_liked ? 'text-yellow-500' : 'text-gray-600 hover:text-red-500'
                    }`}
                  >
                    <FaHeart
                      className={`mr-1 group-hover:scale-125 transition-transform duration-200 ${
                        song.user_has_liked ? 'text-yellow-500' : ''
                      }`}
                    />
                    {song.song_likes}
                  </button>
                  <div className="flex space-x-2">
                    {song.translation && <FaLanguage className="w-5 h-5 text-gray-400" title="Translation available" />}
                    {song.song_vocabulary && <FaBook className="w-5 h-5 text-gray-400" title="Vocabulary available" />}
                  </div>
                </div>

                {/* Report Button (moved here to avoid sibling JSX issues) */}
                <div className="mt-3 flex justify-end">
                  <ReportButton
                    contentId={song.id}
                    contentPreview={`Song: ${song.creator} - ${(song.song_lyrics || '').substring(0, 100)}...`}
                    variant="B"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* No Results Message */}
      {!loading && filteredSongs.length === 0 && selectedCategory !== 'all' && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <FaBook className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Songs Found</h3>
          <p className="text-gray-600 mb-4">
            No songs available in the "{categories.find((c) => c.value === selectedCategory)?.label}" category.
          </p>
          <button
            onClick={() => setSelectedCategory('all')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
          >
            Show All Songs
          </button>
        </div>
      )}

      {/* Access Required Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Access Required</h3>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-600">
                  To access all songs and learning materials, you need to purchase a coupon worth $12. Each coupon provides 30 days of unlimited access to all content on the website.
                </p>
              </div>
              <div className="flex justify-end space-x-4">
                <button onClick={() => setShowAccessModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-900">
                  Cancel
                </button>
                <button onClick={handleBuyCoupon} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Buy Coupon
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
